import React from 'react';
import Configurer from '../../components/Configurer';
import FormViewer from '../../components/FormViewer';
import LoaderButton from '../../components/LoaderButton';
import HttpServices from '../../utils/Http.services';
import { checkTranslation, translate } from '../../utils/useful';
import { siteConfig, invoiceDesign } from '../../variables/config';

const INVOICE_HEADERS = [
  { key: 'orderType', type: 'SelectInput', col: '6', information: { label: '{{lang}}Sale Type', type: 'local', items: [{ value: 'inPerson', title: '{{lang}}In Person' }, { value: 'online', title: '{{lang}}Online' }], placeholder: '{{lang}}Select', required: true }, showMain: true },
  { key: 'user', type: 'AdvancedSelectInput', col: '12', information: { label: '{{lang}}User', address: 'getUsers', filter: {}, fields: { title: 'phone', description: 'fullname', image: 'image', value: '_id' }, type: 'api', isSearchable: true, placeholder: '{{lang}}user', required: false }, showMain: true },
  {
    type: 'SubformInput',
    key: 'list',
    information: {
      label: '{{lang}}items list',
      headers: [
        { key: 'product', type: 'AdvancedSelectInput', col: '4', information: { label: '{{lang}}Product', address: 'getProducts', filter: {}, fields: { title: 'title', image: 'images', value: '_id', barcode: 'barcode', stock: 'stock' }, type: 'api', isSearchable: true, searchByBarcode: true, showStock: true, placeholder: '{{lang}}Product', required: true } },
        { type: 'PriceInput', key: 'price', col: '4', information: { label: '{{lang}}Price', placeholder: '{{lang}}Price', required: true } },
        { type: 'NumberInput', key: 'count', col: '4', information: { label: '{{lang}}Count', placeholder: '{{lang}}Count', required: true } },
      ],
      placeholder: '{{lang}}items list',
      required: true,
    },
    showMain: false,
  },
  { type: 'PriceInput', key: 'totalPrice', col: '6', information: { label: '{{lang}}Total Price', placeholder: '{{lang}}Total Price', required: false }, hideComponent: true },
  { type: 'PriceInput', key: 'deliveryPrice', col: '6', information: { label: '{{lang}}Delivery Price', placeholder: '{{lang}}Delivery Price', required: false }, showMain: true },
  { type: 'PriceInput', key: 'extraDiscount', col: '6', information: { label: '{{lang}}Extra Discount', placeholder: '{{lang}}Extra Discount', required: false }, showMain: true },
  { type: 'SectionInput', key: '*recepient', information: { label: '{{lang}}recepient' }, showMain: false },
  { type: 'TextInput', key: 'recipient.name', col: '6', information: { label: '{{lang}}name', placeholder: '{{lang}}name', required: true }, showMain: false },
  { type: 'TextInput', key: 'recipient.family', col: '6', information: { label: '{{lang}}family', placeholder: '{{lang}}family', required: true }, showMain: false },
  { type: 'NumberInput', key: 'recipient.phone', col: '6', information: { label: '{{lang}}phone', placeholder: '{{lang}}phone', required: true }, showMain: false },
  { type: 'TextInput', key: 'address.address', col: '12', information: { label: '{{lang}}address', placeholder: '{{lang}}address', required: false }, showMain: false },
  { key: 'description', type: 'TextAreaInput', information: { label: '{{lang}}Description', placeholder: '{{lang}}description', required: false }, showMain: false },
];

class AdminInvoiceMaker extends React.Component {
  state = {
    isLoading: false,
    errors: {},
    success: false,
    savedInvoice: null,
    selectedTemplate: invoiceDesign?.defaultTemplate || 'classic',
    printRef: React.createRef(),
    formKey: 0,
  };

  postInvoice = () => {
    if (!this.form) return;
    const data = this.form.getForm();
    if (!data) return;

    this.setState({ isLoading: true, errors: {} });

    const payload = {
      ...data,
      orderType: data.orderType || 'inPerson',
      status: '1',
      paymentStatus: '2',
      transactionStatus: '2',
      deliveryPrice: data.deliveryPrice || 0,
      extraDiscount: data.extraDiscount || 0,
      address: data.address || {},
      recipient: data.recipient || {},
      proccessfinalized: true,
    };

    // Calculate totalPrice from list if not set
    if (payload.list && payload.list.length) {
      payload.totalPrice = payload.list.reduce((sum, i) => sum + (parseFloat(i.price || 0) * parseInt(i.count || 0)), 0) - parseFloat(payload.extraDiscount || 0) + parseFloat(payload.deliveryPrice || 0);
    }

    HttpServices.request('postOrder', payload, (fetchResult, fetchError) => {
      this.setState({ isLoading: false });

      if (fetchError) {
        this.setState({ error: fetchError.message });
        this.props.addNotif({ type: 'error', title: translate('Error'), description: fetchError.message });
        return;
      }

      if (fetchResult?.info) {
        this.setState({ success: true, savedInvoice: fetchResult.info });
        this.props.addNotif({ type: 'success', title: translate('Success'), description: translate('Invoice saved successfully') });
      }
    });
  };

  handlePrint = () => {
    const printContent = this.printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>فاکتور</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Tahoma', 'Arial', sans-serif; direction: rtl; padding: 20px; }
        </style>
      </head>
      <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  createNew = () => {
    this.setState({ success: false, savedInvoice: null, formKey: this.state.formKey + 1 });
  };

  render() {
    const design = invoiceDesign || {};
    const inv = this.state.savedInvoice;
    const template = this.state.selectedTemplate;

    return (
      <Configurer
        settingsInfo={{ showFooter: true, showTabBar: true, showHeader: true, headerTitle: translate('Create Invoice'), button: { goBack: true } }}
        title={translate('Invoices')}
        description={translate('Create invoices for online and in-person sales')}
        className=""
      >
        <div className="container">
          <div className="row m-0 w-100">
            {!this.state.success ? (
              <div className="col-12">
                <div className="mb-4">
                  <label className="font-bold mb-2 d-block">{translate('Print Template')}</label>
                  <div className="flexc gap-2">
                    {['classic', 'minimal', 'modern'].map((t) => (
                      <button
                        key={t}
                        onClick={() => this.setState({ selectedTemplate: t })}
                        className={`px-3 py-2 rounded ${template === t ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{ border: template === t ? '2px solid #677dc4' : '1px solid #ddd' }}
                      >
                        {t === 'classic' && translate('Classic')}
                        {t === 'minimal' && translate('Minimal')}
                        {t === 'modern' && translate('Modern')}
                      </button>
                    ))}
                  </div>
                </div>

                <FormViewer key={this.state.formKey} ref={(el) => (this.form = el)} headers={INVOICE_HEADERS} initData={{ orderType: 'inPerson' }} errors={this.state.errors} variables={this.props.variables} />

                <div className="mt-4 flexc gap-2">
                  <LoaderButton
                    onClick={this.postInvoice}
                    isLoading={this.state.isLoading}
                    text={translate('Save & Print')}
                    className=""
                    buttonStyle={{ padding: '10px 24px', borderRadius: 8 }}
                  />
                </div>
              </div>
            ) : (
              <div className="col-12">
                <div className="alert alert-success mb-3">
                  {translate('Invoice saved successfully')}
                </div>
                <div className="flexc gap-2 mb-3">
                  <button onClick={this.handlePrint} className="btn btn-primary">
                    {translate('Print Invoice')}
                  </button>
                  <button onClick={this.createNew} className="btn btn-outline-secondary">
                    {translate('Create New Invoice')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hidden print content - RTL Farsi */}
        <div ref={this.printRef} style={{ position: 'absolute', left: '-9999px', width: '210mm' }}>
          {inv && (
            <InvoiceTemplate
              invoice={inv}
              design={design}
              template={template}
            />
          )}
        </div>
      </Configurer>
    );
  }
}

function InvoiceTemplate({ invoice, design, template }) {
  const items = invoice.list || [];
  const totalPrice = invoice.totalPrice || 0;
  const deliveryPrice = invoice.deliveryPrice || 0;
  const extraDiscount = invoice.extraDiscount || 0;
  const itemsTotal = items.reduce((s, i) => s + (parseFloat(i.price || 0) * parseInt(i.count || 0)), 0);
  const recipient = invoice.recipient || {};
  const date = invoice.cDate ? new Date(invoice.cDate).toLocaleDateString('fa-IR') : new Date().toLocaleDateString('fa-IR');

  const baseStyles = {
    fontFamily: "'Tahoma', 'Arial', sans-serif",
    direction: 'rtl',
    padding: '15px',
  };

  if (template === 'minimal') {
    return (
      <div style={{ ...baseStyles, border: '1px solid #ddd' }}>
        <div style={{ textAlign: 'center', marginBottom: 15, borderBottom: '1px solid #eee', paddingBottom: 10 }}>
          <h2 style={{ fontSize: 18 }}>{design.shopName || 'فروشگاه'}</h2>
          <p style={{ fontSize: 12, color: '#666' }}>{design.phone}</p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15 }}>
          <span>فاکتور #{invoice._id?.slice(-8) || '-'}</span>
          <span>{date}</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #333' }}>
              <th style={{ padding: 6, textAlign: 'right' }}>ردیف</th>
              <th style={{ padding: 6, textAlign: 'right' }}>کالا</th>
              <th style={{ padding: 6, textAlign: 'right' }}>تعداد</th>
              <th style={{ padding: 6, textAlign: 'right' }}>قیمت</th>
              <th style={{ padding: 6, textAlign: 'right' }}>جمع</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: 6 }}>{i + 1}</td>
                <td style={{ padding: 6 }}>{item.product?.title || '-'}</td>
                <td style={{ padding: 6 }}>{item.count}</td>
                <td style={{ padding: 6 }}>{parseFloat(item.price || 0).toLocaleString('fa-IR')}</td>
                <td style={{ padding: 6 }}>{(parseFloat(item.price || 0) * parseInt(item.count || 0)).toLocaleString('fa-IR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 15, textAlign: 'left' }}>
          <p>جمع کل: {itemsTotal.toLocaleString('fa-IR')} ریال</p>
          {deliveryPrice > 0 && <p>هزینه ارسال: {deliveryPrice.toLocaleString('fa-IR')} ریال</p>}
          {extraDiscount > 0 && <p>تخفیف: {extraDiscount.toLocaleString('fa-IR')} ریال</p>}
          <p style={{ fontWeight: 'bold' }}>مبلغ نهایی: {totalPrice.toLocaleString('fa-IR')} ریال</p>
        </div>
        <div style={{ marginTop: 15, fontSize: 11, color: '#888' }}>
          <p>خریدار: {recipient.name} {recipient.family} - {recipient.phone}</p>
          <p style={{ marginTop: 5 }}>{design.footerText}</p>
        </div>
      </div>
    );
  }

  if (template === 'modern') {
    return (
      <div style={{ ...baseStyles, background: 'linear-gradient(135deg, #f8f9fa 0%, #fff 100%)', border: '1px solid #e0e0e0', borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            {design.logo && <img src={design.logo} alt="" style={{ height: 50 }} />}
            <h2 style={{ fontSize: 20, marginTop: 5 }}>{design.shopName}</h2>
            <p style={{ fontSize: 11, color: '#666' }}>{design.address}</p>
            <p style={{ fontSize: 11 }}>{design.phone}</p>
          </div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ background: '#677dc4', color: '#fff', padding: '8px 16px', borderRadius: 6 }}>
              <span style={{ fontSize: 11 }}>فاکتور فروش</span>
              <div style={{ fontSize: 16, fontWeight: 'bold' }}>#{invoice._id?.slice(-8) || '-'}</div>
            </div>
            <p style={{ marginTop: 8, fontSize: 12 }}>تاریخ: {date}</p>
            <p style={{ fontSize: 12 }}>نوع: {invoice.orderType === 'online' ? 'آنلاین' : 'حضوری'}</p>
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#677dc4', color: '#fff' }}>
              <th style={{ padding: 10, textAlign: 'right', borderRadius: '4px 0 0 0' }}>ردیف</th>
              <th style={{ padding: 10, textAlign: 'right' }}>کالا</th>
              <th style={{ padding: 10, textAlign: 'right' }}>تعداد</th>
              <th style={{ padding: 10, textAlign: 'right' }}>قیمت واحد</th>
              <th style={{ padding: 10, textAlign: 'right', borderRadius: '0 4px 0 0' }}>جمع</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                <td style={{ padding: 10 }}>{i + 1}</td>
                <td style={{ padding: 10 }}>{item.product?.title || '-'}</td>
                <td style={{ padding: 10 }}>{item.count}</td>
                <td style={{ padding: 10 }}>{parseFloat(item.price || 0).toLocaleString('fa-IR')}</td>
                <td style={{ padding: 10 }}>{(parseFloat(item.price || 0) * parseInt(item.count || 0)).toLocaleString('fa-IR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div>
            <p style={{ marginBottom: 5 }}><strong>خریدار:</strong> {recipient.name} {recipient.family}</p>
            <p style={{ marginBottom: 5 }}>تلفن: {recipient.phone}</p>
            {invoice.address?.address && <p>آدرس: {invoice.address.address}</p>}
          </div>
          <div style={{ textAlign: 'left' }}>
            <p>جمع کالاها: {itemsTotal.toLocaleString('fa-IR')}</p>
            {deliveryPrice > 0 && <p>هزینه ارسال: {deliveryPrice.toLocaleString('fa-IR')}</p>}
            {extraDiscount > 0 && <p>تخفیف: {extraDiscount.toLocaleString('fa-IR')}</p>}
            <p style={{ fontSize: 16, fontWeight: 'bold', marginTop: 8 }}>مبلغ قابل پرداخت: {totalPrice.toLocaleString('fa-IR')} ریال</p>
          </div>
        </div>
        <div style={{ marginTop: 25, paddingTop: 15, borderTop: '1px dashed #ddd', textAlign: 'center', fontSize: 12, color: '#888' }}>
          {design.footerText}
        </div>
      </div>
    );
  }

  // Classic template (default)
  return (
    <div style={{ ...baseStyles, border: '2px solid #333' }}>
      <div style={{ borderBottom: '2px solid #333', padding: 15 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {design.logo && <img src={design.logo} alt="" style={{ height: 55 }} />}
            <h1 style={{ fontSize: 22, marginTop: 8 }}>{design.shopName}</h1>
            <p style={{ fontSize: 12 }}>{design.address}</p>
            <p style={{ fontSize: 12 }}>{design.phone}</p>
          </div>
          <div style={{ border: '2px solid #333', padding: 15, textAlign: 'center' }}>
            <div style={{ fontSize: 14, marginBottom: 5 }}>فاکتور فروش</div>
            <div style={{ fontSize: 24, fontWeight: 'bold' }}>#{invoice._id?.slice(-8) || '-'}</div>
            <div style={{ fontSize: 12, marginTop: 5 }}>{date}</div>
            <div style={{ fontSize: 11, marginTop: 3 }}>{invoice.orderType === 'online' ? 'فروش آنلاین' : 'فروش حضوری'}</div>
          </div>
        </div>
      </div>
      <div style={{ padding: 15 }}>
        <div style={{ marginBottom: 15, padding: 10, background: '#f5f5f5' }}>
          <strong>مشخصات خریدار:</strong>
          <span style={{ marginRight: 15 }}>{recipient.name} {recipient.family}</span>
          <span>{recipient.phone}</span>
          {invoice.address?.address && <div style={{ marginTop: 5 }}>آدرس: {invoice.address.address}</div>}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#333', color: '#fff' }}>
              <th style={{ padding: 10, textAlign: 'right' }}>ردیف</th>
              <th style={{ padding: 10, textAlign: 'right' }}>شرح کالا</th>
              <th style={{ padding: 10, textAlign: 'right' }}>تعداد</th>
              <th style={{ padding: 10, textAlign: 'right' }}>قیمت واحد</th>
              <th style={{ padding: 10, textAlign: 'right' }}>مبلغ</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
                <td style={{ padding: 10 }}>{i + 1}</td>
                <td style={{ padding: 10 }}>{item.product?.title || '-'}</td>
                <td style={{ padding: 10 }}>{item.count}</td>
                <td style={{ padding: 10 }}>{parseFloat(item.price || 0).toLocaleString('fa-IR')}</td>
                <td style={{ padding: 10 }}>{(parseFloat(item.price || 0) * parseInt(item.count || 0)).toLocaleString('fa-IR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 20, textAlign: 'left' }}>
          <p>جمع کل کالاها: {itemsTotal.toLocaleString('fa-IR')} ریال</p>
          {deliveryPrice > 0 && <p>هزینه ارسال: {deliveryPrice.toLocaleString('fa-IR')} ریال</p>}
          {extraDiscount > 0 && <p>تخفیف: {extraDiscount.toLocaleString('fa-IR')} ریال</p>}
          <p style={{ fontSize: 16, fontWeight: 'bold', marginTop: 10, borderTop: '2px solid #333', paddingTop: 10 }}>
            مبلغ قابل پرداخت: {totalPrice.toLocaleString('fa-IR')} ریال
          </p>
        </div>
        <div style={{ marginTop: 30, textAlign: 'center', fontSize: 12, color: '#666' }}>
          {design.footerText}
        </div>
      </div>
    </div>
  );
}

export default AdminInvoiceMaker;
